import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { UserGroup } from '@models';
import { MapDetails } from '@models/MapFavorite/mapDetails.model';
import { MapFavoriteInfo } from '@models/MapFavorite/mapFavoriteInfo.model';
import { MapFavoriteRequest } from '@models/MapFavorite/mapFavoriteRequest.model';
import { MapFavoriteService } from '@services/mapFavorite.service';
import { UserService } from '@services/user.service';
import { Authorization } from 'app/shared/classes/authorization';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';

// What the host page would save as a map favorite right now: its current selection and
// the live map. Returned by the host when the user clicks Create / Update.
export interface MapFavoriteSnapshot {
  sensorIDs: number[];
  sensorGroupIDs: number[];
  sensorReadingTypeIDs: number[];
  // Preset label or "start,end" (see dateRangeCodec.encodeDateRange).
  dateRange: string;
  mapDetails: MapDetails | undefined;
}

// The Map Favorite controls shared by GeoMap Readings and the GeoMap tab of Analyze Sensor
// Readings: the favorites dropdown, Save / Delete, and the save dialog.
//
// It owns loading the favorites, permissions, saving and deleting. Applying a favorite to the
// page (restoring the selection and the map view) is the host's job: it listens to
// (favoriteApplied). (selectedChange) reports which favorite is currently selected so the host
// can remember it and enable label editing.
@Component({
  standalone: false,
  selector: 'kig-map-favorite-bar',
  templateUrl: './map-favorite-bar.component.html',
  styleUrls: ['./map-favorite-bar.component.scss']
})
export class MapFavoriteBarComponent implements OnInit, OnChanges {
  @Input() username = '';
  @Input() globalSwitch = false;

  // True when the host has a complete selection that can be saved.
  @Input() canSave = false;

  // The host's current selection, or undefined when none. The host clears it when the user
  // edits the selection by hand (it no longer matches the favorite).
  @Input() selected: MapFavoriteInfo | undefined;

  // A favorite to show as selected once the list has loaded (the one remembered from the
  // last visit), without applying it again. Cleared by the host once it has been resolved.
  @Input() rememberedId: number | undefined;

  // A favorite a link asked to open (e.g. from the home page search). Once the list has loaded it
  // is picked as if the user had chosen it, and it wins over the remembered one.
  @Input() linkedFavoriteId: number | undefined;

  // Called when the user saves, to get the host's current selection and map.
  @Input() snapshot: () => MapFavoriteSnapshot | null = () => null;

  // The user picked a favorite: the host applies its selection and map view.
  @Output() favoriteApplied = new EventEmitter<MapFavoriteInfo>();
  // The selected favorite changed (picked, saved, deleted, cleared, or the remembered one
  // resolved). undefined = no favorite.
  @Output() selectedChange = new EventEmitter<MapFavoriteInfo | undefined>();

  private auth = new Authorization();

  mapFavorites: MapFavoriteInfo[] = [];
  mapFavoritesNamesToDisplay: { id: number; name: string; userName: string; toGroupBy: string }[] = [];
  // The dropdown's model: a list item right after a pick, the full favorite afterwards.
  selectedMapFavorite: any;

  userGroups: UserGroup[] = [];
  associatedUserGroups: UserGroup[] = [];
  selectedUserGroup: UserGroup;
  favoriteName = '';

  modalRef: BsModalRef;
  isModalOpen = false;
  config = {
    backdrop: false,
    ignoreBackdropClick: true
  };

  userGroupconfig: any = {
    displayKey: 'name',
    search: true,
    height: '300px',
    placeholder: 'Select User Group',
    noResultsFound: 'No User Groups found!',
    searchOnKey: 'name'
  };

  private favoriteNameToSelectAfterLoad: string | undefined;
  private favoritesLoaded = false;
  private loadStarted = false;

  constructor(private mapFavoriteService: MapFavoriteService,
              private userService: UserService,
              private modalService: BsModalService,
              private confirmationService: ConfirmationService,
              private toastr: ToastrService) {}


  ngOnInit(): void {
    this.startLoad();
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['username'] != undefined) {
      this.startLoad();
    }

    if (changes['selected'] != undefined) {
      this.selectedMapFavorite = this.selected;
    }

    if (changes['rememberedId'] != undefined) {
      this.resolveRememberedFavorite();
    }
  }


  // ---------- loading ----------

  // Needs the user name (for the groups the user may save to), which the host reads
  // asynchronously, so it starts as soon as that arrives.
  private startLoad(): void {
    if (this.loadStarted || this.username == undefined || this.username === '') {
      return;
    }
    this.loadStarted = true;

    this.userService.getAssociatedUserGroups(this.username).subscribe(
      results => {
        if (results != null) {
          this.associatedUserGroups = results;
          this.loadUserGroups();
        }
      },
      () => {
        this.loadStarted = false;
        this.toastr.error('Error occurred while retrieving user groups.');
      });
  }


  private loadUserGroups(): void {
    this.userService.getUserGroups().subscribe(
      results => {
        if (results != null) {
          this.userGroups = results;
          this.loadFavorites();
        } else {
          this.toastr.warning('No user groups found');
        }
      },
      () => {
        this.toastr.error('Error occurred while retrieving all user groups');
      });
  }


  private loadFavorites(): void {
    this.mapFavoriteService.getMapFavorites().subscribe(
      results => {
        this.mapFavorites = results ?? [];
        this.mapFavorites.forEach(m => this.auth.mapUserAccessForMapFavorite(m, this.globalSwitch, this.username, this.userGroups));
        this.favoritesLoaded = true;
        this.reselectSavedFavorite();
        this.applyLinkedFavorite();
        this.resolveRememberedFavorite();
        this.updateMapFavoritesList(this.globalSwitch);
      },
      () => {
        this.favoritesLoaded = true;
        this.favoriteNameToSelectAfterLoad = undefined;
        this.toastr.error('Error occurred while retrieving map favorites.');
      });
  }


  // After a create / update reloads the list, keep the just-saved favorite selected
  // (matched by name, which the create flow keeps unique).
  private reselectSavedFavorite(): void {
    if (this.favoriteNameToSelectAfterLoad == undefined) {
      return;
    }
    const saved = this.mapFavorites.find(m => m.name === this.favoriteNameToSelectAfterLoad);
    this.favoriteNameToSelectAfterLoad = undefined;

    if (saved != undefined) {
      this.selectedMapFavorite = saved;
      this.selectedChange.emit(saved);
    }
  }


  // Open the favorite a link asked for, the same way a pick does (the host applies it, then is told
  // it is selected). Used once.
  private applyLinkedFavorite(): void {
    if (this.linkedFavoriteId == undefined) {
      return;
    }
    const id = this.linkedFavoriteId;
    this.linkedFavoriteId = undefined;

    const favorite = this.mapFavorites.find(m => m.id === id);
    if (favorite == undefined) {
      this.toastr.warning('That map favorite is no longer available.');
      return;
    }
    this.selectedMapFavorite = favorite;
    this.favoriteApplied.emit(favorite);
    this.selectedChange.emit(favorite);
  }


  // The host remembered which favorite was loaded last time; show it again without applying
  // it (the host's remembered state is newer than the favorite). If it no longer exists the
  // host is told, so it stops remembering it.
  private resolveRememberedFavorite(): void {
    if (this.rememberedId == undefined || !this.favoritesLoaded || this.selectedMapFavorite != undefined) {
      return;
    }
    const saved = this.mapFavorites.find(m => m.id === this.rememberedId);
    this.selectedMapFavorite = saved;
    this.selectedChange.emit(saved);
  }


  updateMapFavoritesList(showAll: boolean): void {
    this.mapFavoritesNamesToDisplay = [];
    this.mapFavorites.forEach(map => {
      if (map.userFeatureAccess["ViewListing"] || showAll) {
        this.mapFavoritesNamesToDisplay.push({
          id: map.id,
          name: map.name,
          userName: map.userName,
          toGroupBy: (map.userGroupId == undefined || map.userGroupId < 1) ? map.userName : map.userGroup
        });
      }
    });

    this.mapFavoritesNamesToDisplay.sort((a, b) => {
      if (b.toGroupBy > a.toGroupBy) { return -1; }
      if (b.toGroupBy < a.toGroupBy) { return 1; }
      if (b.name > a.name) { return -1; }
      if (b.name < a.name) { return 1; }
      return 0;
    });
  }


  // ---------- picking ----------

  favoriteSelectionChanged(): void {
    if (this.selectedMapFavorite == undefined) {
      this.selectedChange.emit(undefined);   // the favorite was cleared
      return;
    }

    const favorite = this.mapFavorites.find(x => x.id === this.selectedMapFavorite.id);
    if (favorite == undefined) {
      return;
    }
    this.selectedMapFavorite = favorite;

    // The host starts applying it before it is told it is selected, so it never caches the
    // new favorite together with the previous page state.
    this.favoriteApplied.emit(favorite);
    this.selectedChange.emit(favorite);
  }


  // ---------- permissions ----------

  isUpdate(): boolean {
    if (this.selectedMapFavorite == undefined) {
      return false;
    }
    return this.selectedMapFavorite.id > 0 && this.selectedMapFavorite.userFeatureAccess?.["EditMapFavorite"] === true;
  }


  canDeleteFavorite(): boolean {
    return this.selectedMapFavorite != undefined && this.selectedMapFavorite.userFeatureAccess?.["DeleteMapFavorite"] === true;
  }


  // ---------- saving ----------

  saveMapFavorite(template: TemplateRef<any>): void {
    if (this.selectedMapFavorite == undefined || this.selectedMapFavorite.userGroupId == undefined || this.selectedMapFavorite.userGroupId < 1) {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == -1);
    } else {
      this.selectedUserGroup = this.associatedUserGroups.find(u => u.id == this.selectedMapFavorite.userGroupId);
    }

    if (this.isUpdate()) {
      this.favoriteName = this.selectedMapFavorite.name;
    }
    if (!this.modalRef || !this.isModalOpen) {
      this.isModalOpen = true;
      this.modalRef = this.modalService.show(template, this.config);
    }
  }


  addOrUpdateMapFavorite(): void {
    const name = (this.favoriteName ?? '').trim();

    if (this.isUpdate()) {
      const request = this.buildFavoriteRequest(name);
      if (request != null) {
        this.updateMapFavorite(request);
      }
    } else if (name === '') {
      this.toastr.error('Please enter a valid name');
      return;
    } else if (this.mapFavorites.some(x => x.name === name)) {
      this.toastr.error('Map favorite name already exists. Please select a new one');
      return;
    } else {
      const request = this.buildFavoriteRequest(name);
      if (request != null) {
        this.addMapFavorite(request);
      }
    }

    this.favoriteName = undefined;
  }


  // The create / update payload from the host's current selection and map. Several groups
  // and loose sensors can be mixed, exactly like a chart favorite.
  private buildFavoriteRequest(name: string): MapFavoriteRequest | null {
    const snapshot = this.snapshot();
    if (snapshot == undefined) {
      return null;
    }

    const mapDetails = JSON.stringify(snapshot.mapDetails ?? {});
    if (mapDetails.length > 8000) {
      this.toastr.error('Too many open pin popups to save. Close some and try again.');
      return null;
    }

    return {
      id: this.isUpdate() ? this.selectedMapFavorite.id : -1,
      name,
      description: null,
      sensorIDs: snapshot.sensorIDs,
      sensorGroupIDs: snapshot.sensorGroupIDs,
      sensorReadingTypeIDs: snapshot.sensorReadingTypeIDs,
      dateRange: snapshot.dateRange,
      mapDetails,
      userName: this.username,
      userGroupId: (this.selectedUserGroup == undefined || this.selectedUserGroup.id < 0) ? null : this.selectedUserGroup.id
    };
  }


  private addMapFavorite(request: MapFavoriteRequest): void {
    this.mapFavoriteService.createMapFavorite(request).subscribe(
      result => {
        if (result != null) {
          this.closeFavoriteModal();
          this.toastr.success('Map favorite created successfully.');
          this.favoriteNameToSelectAfterLoad = request.name;
          this.loadFavorites();
        } else {
          this.toastr.warning('Map favorite was not created.');
        }
      },
      () => {
        this.toastr.error('Error occurred while saving map favorite');
      });
  }


  private updateMapFavorite(request: MapFavoriteRequest): void {
    this.mapFavoriteService.updateMapFavorite(request, this.username).subscribe(
      result => {
        if (result === 'success') {
          this.closeFavoriteModal();
          this.toastr.success('Map favorite updated successfully.');
          this.favoriteNameToSelectAfterLoad = request.name;
          this.loadFavorites();
        } else {
          this.toastr.warning('No map favorites found to update.');
        }
      },
      () => {
        this.toastr.error('Error occurred while updating map favorite.');
      });
  }


  private closeFavoriteModal(): void {
    this.modalRef?.hide();
    this.isModalOpen = false;
  }


  onFavoriteCancelButton(): void {
    this.closeFavoriteModal();
  }


  // ---------- deleting ----------

  onDeleteMapFavoriteConfirm(): void {
    this.confirmationService.confirm({
      message: 'Please confirm that you want to delete the current map favorite',
      key: 'mapFavoriteBarDelete',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.removeMapFavorite();
      },
      reject: () => {
      }
    });
  }


  private removeMapFavorite(): void {
    if (this.selectedMapFavorite == undefined) {
      return;
    }

    this.mapFavoriteService.deleteMapFavorite(this.selectedMapFavorite.id, this.username).subscribe(
      result => {
        if (result === 'success') {
          this.toastr.success('Map favorite deleted');
          this.selectedMapFavorite = undefined;
          this.selectedChange.emit(undefined);
          this.loadFavorites();
        } else {
          this.toastr.warning('No map favorites found.');
        }
      },
      () => {
        this.toastr.error('Error occurred while deleting map favorite.');
      });
  }
}
